// composables/useAuth.ts
import { logSecurityEvent } from '~/composables/useSecurity'

// Sign up with email, password, and optional role (default: 'member')
export const signUp = async (email, password, role = 'member') => {
    const supabase = useSupabaseClient()

    const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: 'http://localhost:3000/auth/confirm' }
    })

    if (error) throw error

    // After sign up, update the user's role in your database (if using auth.users or profiles)
    // await supabase.from('profiles').insert([{ id: data.user?.id, role }])

    return { message: 'Check your email to confirm your account!', user: data.user }
}

// Sign in with email and password
export const signIn = async (email, password, token) => {
    const supabase = useSupabaseClient()
    const { data, error } = await supabase.auth.signInWithPassword({
        email, password, options: {
            captchaToken: token // turnsitekey
        }
    })

    if (error) {
        // Get the user ID from the error message
        const { id = "unknown" } = data.user || {}
        // (Optional) log failed login attempt here
        await logSecurityEvent(null, 'failed_login', `Failed login attempt for ${email}`)

        // increment failed login count in your database
        await supabase
            .from('profiles')
            .update({ failed_logins: { _increment: 1 } })
            .eq('id', id)

        // get num of failed logins
        const { data: profile } = await supabase
            .from('profiles')
            .select('failed_logins')
            .eq('id', id)
            .single()

        // Send a security alert if the user has failed to log in 3 times
        if (profile?.failed_logins >= 3) {
            await supabase.rpc('send_security_alert', {
                subject: '🚨 Multiple Failed Login Attempts',
                message: `User ${email} has failed to log in 3 times.`
            })
        }

        throw error
    }

    // Enforce email confirmation
    if (!data.user?.email_confirmed_at) {
        throw new Error('Please confirm your email before logging in.')
    }

    return data.user
}

// Sign in with a magic link (passwordless)
export const signInWithMagicLink = async (email) => {
    const supabase = useSupabaseClient()
    const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: 'https://your-app.com/magic-confirm' }
    })

    if (error) throw error

    return { message: 'Check your email for the login link!' }
}

// Sign out
export const signOut = async () => {
    const supabase = useSupabaseClient()
    await supabase.auth.signOut()
}

// Fetch the logged-in user's role from the profiles table
export const getUserRole = async () => {
    const supabase = useSupabaseClient()
    const { data: session } = await supabase.auth.getUser()

    if (!session?.user) return null

    const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', session.user.id)
        .single()

    return profile?.role || 'member'
}
